import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, NavLink, Navigate, Outlet, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { authApi, pduApi, zoneLabel } from '../../api/client'
import { useCan } from '../../lib/auth'
import { useCustomerContext } from '../../lib/customer'
import { useCustomers, useJobs, useMaintenanceSessions, useZones } from '../../hooks/useInfra'
import { useMe } from '../AuthGate'
import { Chip } from '../ui/Chip'
import { HeaderSlotsContext } from '../ui/Page'
import { useTasks } from '../ui/Tasks'
import { GlobalSearch } from './GlobalSearch'
import { NAV } from './nav'
import { displayZone, zoneOffsetMinutes } from '../../lib/format'
import { topBarWords } from '../../lib/maintenance'

// AppLayout: dark navigation on the left (the operator area), a top bar with
// search, customer context, who is at maintenance and running activity, the
// page in the middle. The project area has a light header, so
// staff always see in which area they act.
export function AppLayout() {
  const can = useCan()
  const [navOpen, setNavOpen] = useState(false)
  // the top bar's places for the page's breadcrumbs, title, actions and tabs
  const [crumbs, setCrumbs] = useState<HTMLElement | null>(null)
  const [title, setTitle] = useState<HTMLElement | null>(null)
  // the page's actions: beside the title, or at the right end of the tab row
  // when the page has tabs
  const [actionsTop, setActionsTop] = useState<HTMLElement | null>(null)
  const [actionsTabs, setActionsTabs] = useState<HTMLElement | null>(null)
  const [tabs, setTabs] = useState<HTMLElement | null>(null)
  const [controls, setControls] = useState<HTMLElement | null>(null)
  const [tabCount, setTabCount] = useState(0)
  const registerTabs = useCallback(() => {
    setTabCount((n) => n + 1)
    return () => setTabCount((n) => n - 1)
  }, [])
  const actions = tabCount > 0 ? actionsTabs : actionsTop
  const slots = useMemo(() => ({ crumbs, title, actions, tabs, controls, registerTabs }), [crumbs, title, actions, tabs, controls, registerTabs])
  const location = useLocation()
  useEffect(() => setNavOpen(false), [location.pathname, location.search])
  // people without a platform role work in the project area
  if (!can.staff) return <Navigate to="/p" replace />
  // the IP networks are a tab of Network
  const isActive = (to: string) => {
    const p = location.pathname
    if (to === '/ops/network' && p.startsWith('/ops/networks')) return true
    return p === to || p.startsWith(to + '/')
  }

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <aside className={`${navOpen ? 'flex' : 'hidden'} fixed inset-y-0 left-0 z-30 w-[208px] shrink-0 flex-col overflow-y-auto bg-ink text-nav md:sticky md:top-0 md:flex md:h-screen`}>
        <div className="flex items-center gap-2 px-4 pb-2 pt-3">
          <NavLink to="/ops/overview" className="font-display text-[19px] font-bold tracking-tight text-white">xControl</NavLink>
          <span className="rounded-[3px] bg-lime px-1.5 py-0.5 text-[11px] font-extrabold uppercase tracking-wider text-ink" title="Operator area (platform level)">Ops</span>
        </div>
        <nav className="flex-1 px-2.5 pb-3 text-[13px]" aria-label="Main">
          {NAV.map((g, gi) => {
            const items = g.items.filter((i) => !i.administer || can.canAdminister)
            if (items.length === 0) return null
            return (
              <div key={gi} className="mb-2">
                {g.label && <div className="px-2 pb-0.5 text-[10px] font-extrabold uppercase tracking-[0.1em] text-nav-muted">{g.label}</div>}
                {items.map((i) => (
                  <Link key={i.to} to={i.to} aria-current={isActive(i.to) ? 'page' : undefined}
                    className={`flex h-7 items-center rounded-md ${i.indent ? 'pl-5 pr-2 text-[12.5px]' : 'px-2'} ${isActive(i.to) ? 'bg-lime/15 font-extrabold text-lime' : 'font-semibold text-nav hover:bg-white/5 hover:text-white'}`}>
                    {i.label}
                  </Link>
                ))}
              </div>
            )
          })}
        </nav>
        <UserMenu />
      </aside>
      {navOpen && <button className="fixed inset-0 z-20 bg-ink/40 md:hidden" aria-label="Close menu" onClick={() => setNavOpen(false)} />}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* the top bar: where I am and what this page is (breadcrumbs, title,
            actions, tabs - filled by the page) on the left, search and the
            platform's state on the right */}
        <header className="border-b bg-card px-4 md:px-6">
          <div className="flex max-w-[1400px] flex-wrap items-start gap-x-4 gap-y-2 pt-2">
            <button className="rounded-md border px-2 py-1 text-sm md:hidden" onClick={() => setNavOpen(!navOpen)} aria-label="Menu" aria-expanded={navOpen}>☰</button>
            {/* the title keeps some room; else the search and state wrap below */}
            <div className="min-w-[18rem] flex-1">
              <div ref={setCrumbs} className="min-h-[18px]" />
              <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
                <div ref={setTitle} className="min-w-0 flex-1" />
                <div ref={setActionsTop} className="flex shrink-0 flex-wrap items-center gap-2 whitespace-nowrap empty:hidden" />
              </div>
            </div>
            <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
              <GlobalSearch />
              <CustomerSwitch />
              <MaintenanceState />
              <ActivityState />
              <TimeZoneHint />
              <div ref={setControls} className="contents" />
              {can.readOnly && <Chip tone="outline" title="Your role can read but not change anything">Read only</Chip>}
            </div>
          </div>
          <div className="flex max-w-[1400px] flex-wrap items-end gap-x-4 pt-1">
            {/* the tabs keep their line; the actions wrap below when there is no room */}
            <div ref={setTabs} className="max-w-full shrink-0 empty:h-1" />
            <div ref={setActionsTabs} className="ml-auto flex flex-wrap items-center justify-end gap-2 whitespace-nowrap pb-1.5 empty:hidden" />
          </div>
        </header>
        {/* 1400 px of content, as the top bar's rows (plus the padding) */}
        <main className="w-full min-w-0 max-w-[1448px] px-4 py-4 md:px-6">
          <HeaderSlotsContext.Provider value={slots}>
            <Outlet />
          </HeaderSlotsContext.Provider>
        </main>
      </div>
    </div>
  )
}

// MaintenanceState: who is at work on what, for every colleague who logs in (Martin,
// 2026-11-01); without a session, whether a zone is still busy (a rollout, a soak time).
function MaintenanceState() {
  const { data: zones } = useZones()
  const { data: sessions } = useMaintenanceSessions()
  if (!zones && !sessions) return null
  const open = (sessions || []).filter((s) => !s.ended_at)
  const busy = (zones || []).filter((z) => z.maintenance !== 'free')
    .map((z) => ({ label: `${zoneLabel(z.id)} ${z.maintenance}`, activities: (z.activities || []).filter((a) => !a.session_id).map((a) => a.what) }))
  // a zone a session holds is said by the session
  const words = topBarWords(open, busy.filter((z) => z.activities.length > 0))
  return (
    <Link to="/ops/maintenance" title={words.title}
      className={`inline-flex h-8 min-w-0 max-w-full items-center gap-2 rounded-md px-2.5 text-[13px] font-bold sm:max-w-[30rem] ${words.busy ? 'bg-[#FFF1D6] text-[#8A4B00]' : 'bg-[#E2F3E9] text-[#146C43]'}`}>
      <span aria-hidden className={`h-2 w-2 shrink-0 rounded-full ${words.busy ? 'bg-[#C77A00]' : 'bg-[#1E8A55]'}`} />
      <span className="hidden min-w-0 truncate sm:inline">{words.text}</span><span className="min-w-0 truncate sm:hidden">{words.phone}</span>
      {words.age && <span className="shrink-0 font-semibold">{words.age}</span>}
    </Link>
  )
}

// TimeZoneHint: every time in the UI is in the sites' zone; a viewer
// elsewhere sees which zone that is and how far off their own time is
// ("Berlin time · yours −5 h") - handy when talking to people on site.
function TimeZoneHint() {
  const zone = displayZone()
  const mine = Intl.DateTimeFormat().resolvedOptions().timeZone
  const now = new Date()
  const diff = zoneOffsetMinutes(mine, now) - zoneOffsetMinutes(zone, now)
  if (diff === 0) return null
  const city = (zone.split('/').pop() || zone).replace(/_/g, ' ')
  const hours = (m: number) => {
    const h = Math.trunc(Math.abs(m) / 60)
    const min = Math.abs(m) % 60
    return `${m < 0 ? '−' : '+'}${h}${min ? `:${String(min).padStart(2, '0')}` : ''} h`
  }
  const utc = (m: number) => `UTC${m < 0 ? '−' : '+'}${Math.trunc(Math.abs(m) / 60)}${Math.abs(m) % 60 ? `:${String(Math.abs(m) % 60).padStart(2, '0')}` : ''}`
  const clock = (z: string) => now.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit', timeZone: z })
  return (
    <Chip tone="outline" title={`All times are shown in ${zone} (${utc(zoneOffsetMinutes(zone, now))}), the sites' local time. Your browser is in ${mine} (${utc(zoneOffsetMinutes(mine, now))}): your time is ${hours(diff)} from it. Now: ${city} ${clock(zone)} · yours ${clock(mine)} · UTC ${clock('UTC')}`}>
      {city} time · yours {hours(diff)}
    </Chip>
  )
}

// ActivityState: running jobs, linked to the job list.
function ActivityState() {
  const { data: jobs } = useJobs()
  // switching through the PDUs runs on the controller, not as a job
  const { data: ops } = useQuery({ queryKey: ['power-ops'], queryFn: pduApi.powerOps,
    refetchInterval: (q) => (q.state.data?.some((o) => o.state === 'running') ? 3000 : 10000) })
  // and what runs on the PDUs themselves (read, login, firmware update)
  const { data: pduWork } = useQuery({ queryKey: ['pdus-running'], queryFn: pduApi.running, refetchInterval: (q) => (q.state.data?.length ? 3000 : 10000) })
  // and the controller's background tasks (firmware imports, the vendor check)
  const tasksRunning = useTasks().filter((t) => t.state === 'running').length
  const jobsRunning = (jobs || []).filter((j) => j.state === 'pending' || j.state === 'running').length
  const opsRunning = (ops || []).filter((o) => o.state === 'running').length + (pduWork || []).length
  const running = jobsRunning + opsRunning + tasksRunning
  const title = running ? [jobsRunning && `${jobsRunning} job(s)`, opsRunning && `${opsRunning} PDU operation(s)`, tasksRunning && `${tasksRunning} task(s)`].filter(Boolean).join(', ') + ' running' : 'Nothing running'
  return (
    <Link to="/ops/jobs?f_status=active" className="inline-flex h-8 items-center gap-2 whitespace-nowrap rounded-md border px-2.5 text-[13px] font-semibold hover:bg-secondary"
      title={title}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden className="text-primary"><path d="M3 12h4l3-8 4 16 3-8h4" /></svg>
      Activity <span className={`font-mono text-xs ${running ? 'font-bold text-primary' : 'text-muted-foreground'}`}>{running}</span>
    </Link>
  )
}

// CustomerSwitch: the customer level of the context bar. A chosen customer
// filters the infrastructure lists to its resources.
function CustomerSwitch() {
  const { customerId, setCustomerId } = useCustomerContext()
  const { data: customers } = useCustomers()
  if (!customers || customers.length === 0) return null
  const c = customers.find((x) => x.id === customerId)
  return (
    <span className="hidden items-center md:inline-flex">
      <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} aria-label="Customer"
        className={`h-8 rounded-md border px-2 text-[13px] font-semibold ${c ? 'border-[#E7C27A] bg-[#FFF1D6] text-[#8A4B00]' : 'border-input bg-card text-foreground'}`}
        title="Filter the infrastructure lists to one customer">
        <option value="">All customers</option>
        {customers.map((x) => <option key={x.id} value={x.id}>{x.code} · {x.name}</option>)}
      </select>
    </span>
  )
}

function UserMenu() {
  const me = useMe()
  const can = useCan()
  if (!me) return null
  const logout = async () => {
    try {
      await authApi.logout()
    } finally {
      window.location.assign('/')
    }
  }
  return (
    <div className="flex items-center gap-2 border-t border-white/10 px-4 py-2.5">
      <span aria-hidden className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-extrabold uppercase text-white">{me.username.slice(0, 2)}</span>
      <span className="min-w-0 flex-1 leading-tight">
        <span className="block truncate text-[13px] font-bold text-white">{me.username}</span>
        <span className="block truncate text-xs text-nav-muted">{can.roleLabel}{me.source === 'local' ? ' · local' : ''}</span>
        {/* one's own keys: the library, where one's own are marked (an installation lists them all) */}
        {can.canWrite && <Link to="/ops/ssh-keys" className="block truncate text-xs text-nav hover:text-white hover:underline">My SSH keys</Link>}
      </span>
      <button onClick={logout} className="rounded-md border border-white/20 px-2 py-1 text-xs font-semibold text-nav hover:bg-white/10">Log out</button>
    </div>
  )
}
